<template>
    <div :class="['select-plan', { 'select-plan--stepper': stepperMode }]">
        <div v-if="!stepperMode" class="sp-eyebrow">{{ $t('common.plans_and_values') }}</div>
        <div class="sp-title">{{ $t('common.choose_your_plan') }}</div>

        <div class="sp-toggle-row">
            <span class="sp-toggle-label">{{ $t('common.monthly') }}</span>
            <div class="sp-switch" :class="{ monthly: !isYear }" @click="isYear = !isYear">
                <div class="sp-switch-thumb"></div>
            </div>
            <span class="sp-toggle-label">{{ $t('common.annual') }}</span>
        </div>

        <div v-if="withCoupon" class="sp-coupon">
            <div class="sp-coupon-row">
                <input type="text" class="sp-coupon-input" :placeholder="$t('common.write_coupon')"
                       v-model="couponId" :disabled="couponApplied">
                <button class="sp-coupon-apply" :disabled="!couponId" @click="getCouponInfoFromStripe">
                    {{ couponApplied ? $t('common.remove') : $t('common.apply') }}
                    <v-icon size="14">{{ couponApplied ? 'mdi-close' : 'mdi-check' }}</v-icon>
                </button>
            </div>
            <small v-if="messageCoupon" class="sp-coupon-msg">{{ messageCoupon }}</small>
        </div>

        <div v-if="stepperMode" class="ob-plan-grid">
            <subscription-card v-for="(plan, i) in availablePlans" :key="i"
                :plan="plan" :discount="percent_off" :amount-off="amount_off" :savings="savingsFor(plan)"
                :is-active="isActivePlan(plan)" @plan="onChangePlan" stepper-mode/>
        </div>

        <template v-else>
            <div class="sp-nav">
                <v-btn icon @click="prev"><v-icon>mdi-chevron-left</v-icon></v-btn>
                <v-btn icon @click="next"><v-icon>mdi-chevron-right</v-icon></v-btn>
            </div>
            <v-carousel ref="carousel" :show-arrows="false" hide-delimiters touchless height="auto">
                <v-carousel-item v-for="(chunk, index) in chunkedPlans" :key="index">
                    <v-row>
                        <v-col v-for="(plan, i) in chunk" :key="i" cols="12" md="6" lg="4">
                            <subscription-card :plan="plan" :discount="percent_off" :amount-off="amount_off"
                                :savings="savingsFor(plan)"
                                :is-active="isActivePlan(plan)" @plan="onChangePlan"/>
                        </v-col>
                    </v-row>
                </v-carousel-item>
            </v-carousel>
        </template>

        <div v-if="availablePlans.length === 0" class="sp-empty">{{ $t('common.no_available_plans') }}</div>

        <v-overlay z-index="50" :value="loadingCoupon">
            <v-progress-circular color="primary" indeterminate size="64"/>
        </v-overlay>
    </div>
</template>

<script>

import { mapActions, mapState } from "vuex";
import firebase, { db } from "@/utils/Firebase";
import SubscriptionCard from "@/components/elements/cards/SubscriptionCard";

export default {
    name: "SelectPlan",
    components: { SubscriptionCard },
    props: {
        withCoupon: {
            type: Boolean,
            default: false,
        },
        stepperMode: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            isYear: false,
            couponId: null,
            loadingCoupon: false,
            percent_off: null,
            amount_off: null,
            messageCoupon: '',
            couponApplied: false,
            selectedPlan: null,
            legacyPlan: null,
        }
    },
    computed: {
        ...mapState(['plans']),
        availablePlans(vm = this) {
            let plans = vm.plans?.filter(plan => vm.isYear ? plan.intervalType === 'annual' : plan.intervalType === 'monthly') || []
            let showGrowthPlan = !('firstPlan' in (vm.account?.stripeAccount || {})) || vm.account?.stripeAccount?.fromFree === true || vm.account?.stripeAccount?.currentSubscription?.isFree === true || vm.account?.overrides?.allowFirstPlanOnly === true || false
            if (!showGrowthPlan) plans = plans.filter(plan => !plan.firstPlanOnly || plan.priceId === vm.priceId)
            const legacy = vm.legacyPlan
            if (!vm.stepperMode && legacy && legacy.priceId === vm.priceId && legacy.intervalType === (vm.isYear ? 'annual' : 'monthly')) {
                plans = [legacy, ...plans]
            }
            return plans
        },
        priceId(vm = this) {
            return vm.account?.stripeAccount?.currentSubscription?.priceId || null
        },
        chunkedPlans() {
            let size = 1
            if (this.$vuetify.breakpoint.mdAndUp) size = 2
            if (this.$vuetify.breakpoint.lgAndUp) size = 3
            const chunks = []
            for (let i = 0; i < this.availablePlans.length; i += size) {
                chunks.push(this.availablePlans.slice(i, i + size))
            }
            return chunks
        }
    },
    async mounted() {
        const vm = this
        await vm.loadPlans()
        // console.log('select', vm)
    },
    methods: {
        ...mapActions(['bindPlans', 'bindCompanyPlans']),
        async loadPlans() {
            const vm = this
            if (vm.plans.length === 0 && vm.account) {
                vm.account.accountType === 'company' ?
                    await vm.bindCompanyPlans() :
                    await vm.bindPlans()
            }
            await vm.loadLegacyPlan()
        },
        async loadLegacyPlan() {
            const vm = this
            const priceId = vm.priceId
            if (vm.stepperMode || !priceId || vm.plans.some(plan => plan.priceId === priceId)) return vm.legacyPlan = null
            if (vm.legacyPlan?.priceId === priceId) return
            try {
                const { docs } = await db.collection('plans').where('priceId', '==', priceId).limit(1).get()
                vm.legacyPlan = docs.length > 0 ? { id: docs[0].id, ...docs[0].data() } : null
                if (vm.legacyPlan?.intervalType === 'annual') vm.isYear = true
            } catch (error) {
                console.error('[select-plan] loadLegacyPlan', error?.code, error?.message)
                vm.legacyPlan = null
            }
        },
        isActivePlan(plan) {
            const vm = this
            if (vm.stepperMode) return plan.priceId === vm.selectedPlan?.priceId
            return plan.priceId === vm.priceId
        },
        sameTier(a, b) {
            if (a.name && b.name && a.name === b.name) return true
            return a.maxClients != null && b.maxClients != null && a.maxClients === b.maxClients
        },
        savingsFor(plan) {
            const vm = this
            if (!vm.isYear || plan.isFree) return null
            const monthly = (vm.plans || []).find(p => p.intervalType === 'monthly' && vm.sameTier(p, plan))
            if (!monthly) return null
            const save = Math.round((monthly.amount * 12) - plan.amount)
            return save > 0 ? save : null
        },
        onChangePlan(plan) {
            const vm = this
            vm.selectedPlan = plan
            vm.$emit('change', plan)
            vm.$emit('plan')
        },
        getCouponInfo: firebase.functions().httpsCallable('httpPayments-getCouponInfo'),
        async getCouponInfoFromStripe() {
            const vm = this

            if (!vm.couponId) return

            if (vm.couponApplied) {
                vm.couponId = null
                vm.percent_off = null
                vm.amount_off = null
                vm.couponApplied = false
                vm.$emit('coupon', null)
                vm.messageCoupon = ''
                return
            }

            vm.loadingCoupon = true
            // console.log('couponId', vm.couponId)
            await vm.getCouponInfo({ couponId: vm.couponId }).then(result => {
                // console.log('coupon result', result)
                let data = result.data
                if (data.id) {
                    if (data.times_redeemed === data.max_redemptions) return vm.messageCoupon = vm.$t('payment_failed_messages.coupon_maximum_redeem')
                    if (!data.valid) return vm.messageCoupon = vm.$t('payment_failed_messages.coupon_invalid')
                    vm.messageCoupon = `${vm.$t('common.coupon_obtained')} ${data.name}`
                    vm.percent_off = data.percent_off
                    vm.amount_off = data.amount_off ? data.amount_off / 100 : null
                    vm.couponApplied = true
                    vm.$emit('coupon', data.id)
                } else {
                    vm.messageCoupon = data.message
                }
            }).catch(err => {
                console.log(`Error cupón: ${err.message}`)
                vm.messageCoupon = vm.$t('payment_failed_messages.coupon_invalid')
            }).finally(() => {
                vm.loadingCoupon = false
            })
        },
        next() {
            this.$refs.carousel.next()
        },
        prev() {
            this.$refs.carousel.prev()
        }
    },
    watch: {
        account() {
            this.loadPlans()
        }
    }
}
</script>

<style scoped>
.select-plan {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.sp-eyebrow {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-accent-base);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 6px;
}

.sp-title {
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.2;
    text-align: center;
    margin-bottom: 20px;
}

.select-plan--stepper .sp-title {
    text-align: left;
    margin-bottom: 16px;
}

.sp-toggle-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 18px;
}

.select-plan--stepper .sp-toggle-row {
    justify-content: flex-start;
}

.sp-toggle-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.sp-switch {
    width: 46px;
    height: 25px;
    border-radius: 13px;
    background: var(--v-primary-base);
    position: relative;
    cursor: pointer;
    flex-shrink: 0;
}

.sp-switch-thumb {
    position: absolute;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    right: 3px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: transform .2s ease;
}

.sp-switch.monthly .sp-switch-thumb {
    transform: translateX(-21px);
}

.sp-coupon {
    margin-bottom: 22px;
}

.sp-coupon-row {
    display: flex;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    overflow: hidden;
}

.sp-coupon-input {
    flex: 1;
    min-width: 0;
    height: 42px;
    border: none;
    background: var(--v-field-base);
    font-size: 14px;
    font-family: inherit;
    color: inherit;
    padding: 0 14px;
    outline: none;
}

.sp-coupon-input::placeholder {
    color: var(--v-txtMuted-base);
}

.sp-coupon-apply {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 14px;
    height: 42px;
    border: none;
    border-left: 1px solid var(--v-outline-base);
    background: var(--v-field-base);
    color: var(--v-accent-base);
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
}

.sp-coupon-apply:disabled {
    color: var(--v-txtMuted-base);
    cursor: default;
}

.sp-coupon-msg {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    text-align: center;
}

.ob-plan-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
}

.sp-nav {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.sp-empty {
    text-align: center;
    color: var(--v-txtMuted-base);
    margin-top: 16px;
}
</style>